import { create } from 'zustand';
import { ThirdPartyIntegrationService, IntegrationConnectionDTO } from '../service/ThirdPartyIntegrationService';

export type IntegrationConnection = IntegrationConnectionDTO;

type ThirdPartyIntegrationState = {
  connectionsByProvider: Record<string, IntegrationConnection[]>;
  connectedProviders: Record<string, boolean>;

  fetchConnections: (provider: string) => Promise<void>;
  hasConnections: (provider: string) => boolean;
  getConnections: (provider: string) => IntegrationConnection[];
  addConnection: (provider: string, conn: IntegrationConnection) => void;
  removeConnection: (provider: string, integrationId: string) => void;
  setConnectionSharing: (
    provider: string,
    integrationId: string,
    shared: boolean,
  ) => Promise<void>;

  setProviderConnected: (provider: string, connected: boolean) => void;
  isProviderConnected: (provider: string) => boolean;
  setConnectedProviders: (providers: Record<string, boolean>) => void;
};

export const useStoreThirdPartyIntegration = create<ThirdPartyIntegrationState>(
  (set, get) => ({
    connectionsByProvider: {},
    connectedProviders: {},

    fetchConnections: async (provider: string) => {
      try {
        const response = await ThirdPartyIntegrationService.getConnections(provider);
        const connections = (response?.data as any) ?? [];
        const list = Array.isArray(connections) ? connections : [];
        set((state) => ({
          connectionsByProvider: {
            ...state.connectionsByProvider,
            [provider]: list,
          },
          connectedProviders: {
            ...state.connectedProviders,
            [provider]: list.length > 0,
          },
        }));
      } catch {
        set((state) => ({
          connectionsByProvider: {
            ...state.connectionsByProvider,
            [provider]: [],
          },
          connectedProviders: {
            ...state.connectedProviders,
            [provider]: false,
          },
        }));
      }
    },

    hasConnections: (provider: string) => {
      const list = get().connectionsByProvider[provider];
      return Array.isArray(list) && list.length > 0;
    },

    getConnections: (provider: string) => {
      return get().connectionsByProvider[provider] ?? [];
    },

    addConnection: (provider: string, conn: IntegrationConnection) => {
      set((state) => {
        const existing = state.connectionsByProvider[provider] ?? [];
        return {
          connectionsByProvider: {
            ...state.connectionsByProvider,
            [provider]: [...existing, conn],
          },
          connectedProviders: {
            ...state.connectedProviders,
            [provider]: true,
          },
        };
      });
    },

    removeConnection: (provider: string, integrationId: string) => {
      set((state) => {
        const existing = state.connectionsByProvider[provider] ?? [];
        const filtered = existing.filter((c) => c.id !== integrationId);
        return {
          connectionsByProvider: {
            ...state.connectionsByProvider,
            [provider]: filtered,
          },
          connectedProviders: {
            ...state.connectedProviders,
            [provider]: filtered.length > 0,
          },
        };
      });
    },

    setConnectionSharing: async (
      provider: string,
      integrationId: string,
      shared: boolean,
    ) => {
      const previous = get().connectionsByProvider[provider] ?? [];
      const optimistic = previous.map((c) =>
        c.id === integrationId ? { ...c, isSharedWithOrg: shared } : c,
      );
      set((state) => ({
        connectionsByProvider: {
          ...state.connectionsByProvider,
          [provider]: optimistic,
        },
      }));
      try {
        await ThirdPartyIntegrationService.setSharing(
          provider,
          integrationId,
          shared,
        );
      } catch (error) {
        set((state) => ({
          connectionsByProvider: {
            ...state.connectionsByProvider,
            [provider]: previous,
          },
        }));
        throw error;
      }
    },

    setProviderConnected: (provider: string, connected: boolean) =>
      set((state) => ({
        connectedProviders: {
          ...state.connectedProviders,
          [provider]: connected,
        },
      })),

    isProviderConnected: (provider: string) =>
      Boolean(get().connectedProviders[provider]),

    setConnectedProviders: (providers: Record<string, boolean>) =>
      set({ connectedProviders: providers }),
  }),
);
